import { ReactNode, useCallback, useMemo, Key as ReactKey, type JSX } from "react";
import { ComboBox as AriaComboBox } from "react-aria-components";
import { useLabel } from "react-aria";

import { AnalyticsCommonProps } from "hooks/useAnalytics";
import useEnsureId from "hooks/useEnsureId";
import { getColors } from "ds/components/Form/Field/utils";
import TooltipInfo from "ds/components/TooltipInfo";
import ListBoxPopover from "ds/components/ListBox/Popover";
import ListBox from "ds/components/ListBox";
import ListBoxScrollableWrapper from "ds/components/ListBox/ScrollableWrapper";
import FormFieldHelperText from "ds/components/Form/Field/HelperText";
import Box from "ds/components/Box";
import FormFieldAriaLabel from "ds/components/Form/Field/AriaLabel";
import ComboBoxEmptyCollection from "ds/components/ComboBox/EmptyCollection";
import { BaseCollection, SectionBaseCollection } from "ds/components/ComboBox/types";
import useSkipDuplicates from "ds/components/ComboBox/useSkipDuplicates";
import DropdownFooter from "ds/components/Dropdown/Footer";
import DropdownSelectClearAll from "ds/components/Dropdown/SelectClearAll";
import styles from "ds/components/ComboBox/styles.module.css";

import {
  flattenComboBoxMultipleItems,
  flattenComboBoxMultipleSelectableValues,
  isComboBoxMultipleAllSelected,
} from "./flattenSelectableValues";
import ComboBoxMultipleInput, { ComboBoxMultipleInputProps } from "./Input";

type ComboBoxMultipleProps<
  Collection extends BaseCollection | SectionBaseCollection<BaseCollection, SectionProps>,
  Key extends string,
  SectionProps extends object = object,
> = {
  id?: string;
  /**
   * label is optional, but if it's not provided you need to connect the label via ariaLabelledBy attribute
   */
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: string;
  values?: string[] | null;
  items: Array<Collection>;
  onChange: (value: Key | null) => void;
  inputValue?: string;
  renderInput?: (input: ComboBoxMultipleInputProps<Collection, SectionProps>) => ReactNode;
  onInputChange?: (value: string) => void;
  children: (Collection: Collection) => JSX.Element;
  tooltipInfo?: ReactNode;
  tooltipAnalyticsPage?: AnalyticsCommonProps["analyticsPage"];
  tooltipAnalyticsTitle?: AnalyticsCommonProps["analyticsTitle"];
  tooltipAnalyticsProps?: AnalyticsCommonProps["analyticsProps"];
  isDisabled?: boolean;
  isLoading?: boolean;
  size?: ComboBoxMultipleInputProps<Collection, SectionProps>["size"];
  color?: ComboBoxMultipleInputProps<Collection, SectionProps>["color"];
  /**
   * When provided, shows a dropdown footer with "N selected", **Select all**, and **Clear all**
   * bulk actions. Receives the full selection at once.
   */
  onBulkValuesChange?: (values: string[]) => void;
  /** When false, **Select all** is hidden; **Clear all** still appears when something is selected. Defaults to true. */
  footerShowSelectAll?: boolean;
};

const ComboBoxMultiple = <
  Collection extends BaseCollection | SectionBaseCollection<BaseCollection, SectionProps>,
  Key extends string,
  SectionProps extends object = object,
>({
  id: propsId,
  children,
  label,
  helperText,
  placeholder = "Type in or select from the list",
  error,
  values,
  size,
  color,
  renderInput = (props) => <ComboBoxMultipleInput {...props} />,
  onChange,
  inputValue,
  onInputChange,
  items,
  tooltipInfo,
  tooltipAnalyticsPage,
  tooltipAnalyticsTitle,
  tooltipAnalyticsProps,
  isDisabled,
  isLoading,
  onBulkValuesChange,
  footerShowSelectAll = true,
}: ComboBoxMultipleProps<Collection, Key, SectionProps>) => {
  const id = useEnsureId(propsId);
  const { primaryIconColor } = getColors(color);

  const handleSelectionChange = useCallback(
    (value: ReactKey | null) => {
      if (value === null || value === undefined) {
        return onChange(value);
      }

      return onChange(String(value) as Key);
    },
    [onChange]
  );

  const deduplicatedItems = useSkipDuplicates(items);
  // Section leaves are nested under `.items`, which never changes reference when only the
  // selection changes — react-aria caches collection nodes by item identity, so without a
  // fresh reference here it keeps rendering their checkboxes with stale `isSelected` state.
  const deduplicatedItemsWithSelection = deduplicatedItems.map((item) => {
    if ("items" in item) {
      return {
        ...item,
        items: item.items.map((nestedItem) => ({
          ...nestedItem,
          isSelected: values?.includes(nestedItem.value),
        })),
      };
    }

    return {
      ...item,
      isSelected: "value" in item && values?.includes(item.value),
    };
  });

  const selectableValues = useMemo(
    () => flattenComboBoxMultipleSelectableValues(flattenComboBoxMultipleItems(items)),
    [items]
  );
  const allSelected = useMemo(
    () => isComboBoxMultipleAllSelected(selectableValues, values),
    [selectableValues, values]
  );

  const { labelProps, fieldProps } = useLabel({
    label,
    id,
  });

  return (
    <Box gap="small" direction="column" fullWidth>
      {label && (
        <Box gap="small" align="center" fullWidth>
          <FormFieldAriaLabel {...labelProps} label={label} color={color} />
          {tooltipInfo && (
            <TooltipInfo
              variant="modal"
              iconColor={primaryIconColor}
              analyticsPage={tooltipAnalyticsPage}
              analyticsTitle={tooltipAnalyticsTitle}
              analyticsProps={tooltipAnalyticsProps}
            >
              {tooltipInfo}
            </TooltipInfo>
          )}
        </Box>
      )}
      <AriaComboBox
        className={styles.comboBox}
        menuTrigger="focus"
        selectedKey={null}
        onSelectionChange={handleSelectionChange}
        inputValue={inputValue}
        onInputChange={onInputChange}
        // react-aria only skips its own recursive contains-filter (which hangs on sectioned,
        // nested collections) when it's given `items` directly — treated as "the consumer
        // filters this themselves". Only opt in when a consumer controls inputValue, since
        // that's the signal they're filtering `items` on every keystroke; everyone else keeps
        // relying on the built-in filter, unchanged.
        items={inputValue !== undefined ? items : undefined}
        isInvalid={!!error}
        isDisabled={isDisabled}
        allowsEmptyCollection
        {...fieldProps}
      >
        <Box gap="small" direction="column">
          {renderInput({
            id,
            placeholder,
            isError: !!error,
            isDisabled,
            isLoading,
            size,
            color,
            values,
            items,
          })}
          <FormFieldHelperText helperText={helperText} error={error} id={id} color={color} />
        </Box>

        <ListBoxPopover>
          <ListBoxScrollableWrapper>
            <ListBox<Collection>
              items={deduplicatedItemsWithSelection}
              renderEmptyState={() => <ComboBoxEmptyCollection isLoading={isLoading} />}
            >
              {children}
            </ListBox>
          </ListBoxScrollableWrapper>
          {onBulkValuesChange && !isDisabled && (
            <DropdownFooter>
              <DropdownSelectClearAll
                selectedLength={values?.length ?? 0}
                allSelected={allSelected}
                exclusiveSelectOrClearLinks
                onResetAll={() => onBulkValuesChange([])}
                onSelectAll={
                  footerShowSelectAll && selectableValues.length > 0
                    ? () => onBulkValuesChange(selectableValues)
                    : undefined
                }
              />
            </DropdownFooter>
          )}
        </ListBoxPopover>
      </AriaComboBox>
    </Box>
  );
};

export default ComboBoxMultiple;
